import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import {
  type OwnedPrompt,
  type PromptFields as ApiPromptFields,
  ownedPromptsApi,
} from '../../api/owned-prompts'
import { useDialog } from '../../components/dialogs/useDialog'
import PromptEditorDialog from '../../components/prompts/PromptEditorDialog'
import PromptMasterDetail from '../../components/prompts/PromptMasterDetail'

const buttonClass =
  'rounded-lg border border-harbor-border px-2.5 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50 hover:text-emerald-400'

/**
 * Super-admin catalog maintenance. Uses the same master/detail UX as the
 * org/project prompt workspace: searchable list on the left, selected
 * prompt details on the right, selection in the URL (?prompt=<id>).
 * Guarded by SuperAdminRoute in App.tsx.
 *
 * Built-in records are global (orgId NULL server-side): creating here
 * writes to the shared built-in catalog, and any org admin can import
 * them into their own org via Import Prompts (copy builtin -> organization).
 */
export default function BuiltinPromptsPage() {
  const [params, setParams] = useSearchParams()
  const selectedId = params.get('prompt')
  const onSelect = useCallback(
    (id: string | null) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id === null) next.delete('prompt')
          else next.set('prompt', id)
          return next
        },
        { replace: true }
      )
    },
    [setParams]
  )
  const [prompts, setPrompts] = useState<OwnedPrompt[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<OwnedPrompt | 'new' | null>(null)
  const dialog = useDialog()

  const load = useCallback(async () => {
    setError(null)
    try {
      const page = await ownedPromptsApi.list({ view: 'builtin' })
      setPrompts(page.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the built-in catalog')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function handleSave(fields: ApiPromptFields) {
    if (editing === 'new') {
      await ownedPromptsApi.create({ kind: 'builtin' }, fields)
    } else if (editing) {
      await ownedPromptsApi.update(editing.id, fields, editing.updatedAt)
    }
    setEditing(null)
    await load()
  }

  async function handleArchive(prompt: OwnedPrompt) {
    const confirmed = await dialog.confirm({
      title: `Delete "${prompt.title}"?`,
      description: 'This removes it from the built-in catalog for every organization.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await ownedPromptsApi.archive(prompt.id)
      toast.success('Prompt deleted')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  if (error !== null) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-10 text-center">
        <p className="text-sm font-medium text-red-300">{error}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm font-medium text-harbor-text transition-colors hover:border-emerald-500/50 hover:text-emerald-400"
        >
          Retry
        </button>
      </div>
    )
  }
  if (prompts === null) {
    return (
      <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">
        Loading built-in catalog…
      </p>
    )
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-harbor-text">Built-in Prompt Catalog</h1>
          <p className="mt-1 text-sm text-harbor-text-secondary">
            Available to every organization via Import Prompts.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
        >
          New prompt
        </button>
      </div>

      <div className="mt-4">
        <PromptMasterDetail
          prompts={prompts}
          scopeLabel={() => 'Built-in'}
          selectedId={selectedId}
          onSelect={onSelect}
          emptyState={
            <p className="mt-10 text-center text-sm text-harbor-text-secondary">
              No built-in prompts yet. Create one to get started.
            </p>
          }
          renderActions={(prompt) => (
            <>
              <button type="button" onClick={() => setEditing(prompt)} className={buttonClass}>
                Edit
              </button>
              <button
                type="button"
                onClick={() => void handleArchive(prompt)}
                className="rounded-lg border border-red-500/40 px-2.5 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/70"
              >
                Delete
              </button>
            </>
          )}
        />
      </div>

      {editing !== null && (
        <PromptEditorDialog
          title={editing === 'new' ? 'New built-in prompt' : `Edit "${editing.title}"`}
          initial={
            editing === 'new'
              ? { title: '', description: null, body: '' }
              : { title: editing.title, description: editing.description, body: editing.body }
          }
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
